import { useGetTimeSeriesPlotDataQuery } from '../../../../services/mVPGraphQlService';
import {
  AppReactECharts,
  DatePickerController,
} from '../../../../components';
import dayjs from 'dayjs';
import * as yup from 'yup';
import { useFormWithSchema } from '../../../../utils/functions/yupFormTypescript';
import { Box, Grid, Typography } from '@mui/material';
import { useGetFilterDataForMapQuery } from '../../../../services/mVPMapService';
import { useEffect, useRef } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { MVPFiltersProps } from '../../constants';
import TimeSeriesChartTable from '../table/TimeSeriesChartTable';
import { calculateDateDifference } from '../../../../utils/constants/calendar';
import { useFilterContext } from '../../../../services/mVPMapService/filterContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import React from 'react';

const currDate = new Date();
const formSchema = yup.object({
  fieldNumber: yup.number(),
  startDate: yup
    .date()
    .default(dayjs(currDate).subtract(100, 'days').toDate())
    .required('Start Date is required'),
  endDate: yup
    .date()
    .default(dayjs(currDate).toDate())
    .required('End Date is required'),
});

export const TimeSeriesPlot: React.FC<MVPFiltersProps> = ({
  fieldId: fieldId,
  sowingTimeInSeconds,
  havestTimeInSeconds,
  selectedIndex,
}) => {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormWithSchema(formSchema, {
    mode: 'onTouched',
  });
  const { filterValues: commonFilters } =
    useFilterContext();
  const wsId = useSelector(
    (state: RootState) => state.country.wsId
  );

  const startTime = Math.floor(
    dayjs(watch('startDate')).startOf('day').valueOf() /
      1000
  );
  const endTime = Math.floor(
    dayjs(watch('endDate')).endOf('day').valueOf() / 1000
  );
  const currentTimeRef = useRef(
    Math.floor(new Date().getTime() / 1000)
  );
  const currentTime = currentTimeRef.current;

  const {
    data: filtersDataObject,
    isFetching: isLoadingFilters,
  } = useGetFilterDataForMapQuery(
    {
      commonFilters,
      wsId,
      selectedIndex,
      startTime:
        commonFilters?.season == 'R2025'
          ? 1757462400
          : 1743465600,
      endTime: currentTime,
      filterTypes: ['FILTER_FIELD_NUMBER'],
    },
    {
      skip: !selectedIndex || !commonFilters?.season,
    }
  );

  const filtersData =
    filtersDataObject?.data?.GetDataForTimeRangeWithFilters;

  let fieldNumberFilterOptions =
    filtersData?.fieldNumbers?.map((fieldNumber) => ({
      value: fieldNumber,
      label: fieldNumber.toString(),
    })) || [];
  const selectedFarm = fieldId;

  if (
    fieldId &&
    !fieldNumberFilterOptions.some(
      (ele) => ele.value === fieldId
    )
  ) {
    fieldNumberFilterOptions = [
      ...fieldNumberFilterOptions,
      { value: fieldId, label: fieldId.toString() },
    ];
  }

  useEffect(() => {
    if (fieldId) {
      setValue('fieldNumber', fieldId);
    } else if (
      !fieldNumberFilterOptions?.find(
        (ele) => ele.value === selectedFarm
      )
    ) {
      setValue('fieldNumber', undefined);
    }
  }, [fieldId, filtersDataObject]);

  const fieldStartDateSetRef = useRef<
    Record<number, boolean>
  >({});
  const fieldEndDateSetRef = useRef<
    Record<number, boolean>
  >({});

  useEffect(() => {
    if (
      selectedFarm &&
      sowingTimeInSeconds &&
      !fieldStartDateSetRef.current[selectedFarm]
    ) {
      setValue(
        'startDate',
        dayjs(sowingTimeInSeconds * 1000).toDate()
      );
      fieldStartDateSetRef.current[selectedFarm] = true;
    }
    if (
      selectedFarm &&
      havestTimeInSeconds &&
      !fieldEndDateSetRef.current[selectedFarm]
    ) {
      setValue(
        'endDate',
        dayjs(havestTimeInSeconds * 1000).toDate()
      );
      fieldEndDateSetRef.current[selectedFarm] = true;
    }
  }, [
    selectedFarm,
    sowingTimeInSeconds,
    setValue,
    havestTimeInSeconds,
  ]);

  useEffect(() => {
    if (selectedFarm) {
      fieldStartDateSetRef.current[selectedFarm] = false;
      fieldEndDateSetRef.current[selectedFarm] = false;
    }
  }, [selectedFarm]);

  const { data: plotData, isFetching: isLoadingPlotData } =
    useGetTimeSeriesPlotDataQuery(
      (selectedFarm
        ? {
            commonFilters,
            selectedIndex,
            fieldNumber: selectedFarm,
            startTime,
            endTime,
            wsId,
          }
        : undefined) ?? skipToken
    );

  // const fieldNumbers =
  //   plotData?.data?.GetTimeSeriesDetailsForFieldNumber
  //     ?.fieldNumbers;
  const xAxisData =
    plotData?.data?.GetTimeSeriesDetailsForFieldNumber
      ?.xAxis;
  const yAxisData =
    plotData?.data?.GetTimeSeriesDetailsForFieldNumber
      ?.yAxis;
  const options = {
    // grid: { top: 8, right: 8, bottom: 24, left: 36 },
    xAxis: {
      type: 'category',
      data: xAxisData?.map((date) =>
        dayjs(date).format('DD/MM/YYYY')
      ),
      name: '',
      nameTextStyle: {
        fontWeight: 'bolder',
        fontSize: '14',
      },
    },
    yAxis: {
      type: 'value',
      name: selectedIndex,
      nameTextStyle: {
        fontWeight: 'bolder',
        fontSize: '14',
      },
    },
    series: [
      {
        data: yAxisData,
        type: 'line',
        smooth: true,
      },
    ],
    tooltip: {
      trigger: 'axis',
      formatter: (
        params: { axisValueLabel: string; data: number }[]
      ) => {
        const date = params[0].axisValueLabel;
        const value = params[0].data;

        const startDate = sowingTimeInSeconds
          ? dayjs(sowingTimeInSeconds * 1000).format(
              'DD/MM/YYYY'
            )
          : '-';
        const endDate = date;
        const difference = calculateDateDifference(
          startDate,
          endDate
        );

        const additionalData =
          difference?.days < 0
            ? `Days before sowing: ${Math.abs(
                difference.days
              )}`
            : `Days after sowing: ${difference.days}`;

        return `
        <div>
          <strong>Date:</strong> ${date}<br/>
          <strong>Value:</strong> ${value}<br/>
          <strong>${additionalData}</strong>
        </div>
      `;
      },
    },
    dataZoom: [
      {
        type: 'slider',
        xAxisIndex: 0,
        filterMode: 'none',
        minValueSpan: 1, // Ensure at least 1 units remain visible on zoom
      },
    ],
  };

  return (
    <>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, fontSize: 14 }}>
        Time series Plot ({selectedIndex})
      </Typography>
      <Grid container spacing={2}>
        {/* <Grid
          item
          xs={6}
          sm={6}
          md={2}
          lg={2}
          className="field-grid ">
          <AutoCompleteController
            label="Field ID"
            name="fieldNumber"
            control={control}
            options={fieldNumberFilterOptions}
            error={errors.fieldNumber?.message}
            disableClearable
          />
        </Grid> */}

        <Grid
          item
          xs={6}
          sm={6}
          md={4}
          lg={4}
          className="field-grid ">
          <DatePickerController
            label="Start Date"
            control={control}
            required
            name="startDate"
            error={errors.startDate?.message}
            selectsStart
            startDate={watch('startDate')}
            endDate={watch('endDate')}
            maxDate={watch('endDate')}
          />
        </Grid>
        <Grid
          item
          xs={6}
          sm={6}
          md={4}
          lg={4}
          className="field-grid">
          <DatePickerController
            label="End Date"
            control={control}
            required
            name="endDate"
            error={errors.endDate?.message}
            selectsEnd
            startDate={watch('startDate')}
            endDate={watch('endDate')}
            minDate={watch('startDate')}
            maxDate={new Date()}
          />
        </Grid>
      </Grid>
      {isLoadingPlotData || isLoadingFilters ? (
        <></>
      ) : (
        <div style={{ marginTop: -30 }}>
          {selectedFarm && xAxisData?.length ? (
            <AppReactECharts
              option={options}
              startTime={startTime}
              endTime={endTime}
              commonFilters={commonFilters}
              selectedIndex={selectedIndex}
              fieldNumber={watch('fieldNumber')}
              plotType="timeSeriesPlot"
            />
          ) : !selectedFarm ? (
            <Box className="no-data-text text-center mt-3">
              please select a Field
            </Box>
          ) : (
            !xAxisData?.length && (
              <Box className="no-data-text  mt-3">
                No Data Found In Graph
              </Box>
            )
          )}
          <TimeSeriesChartTable
            fieldNumbers={
              selectedFarm ? [selectedFarm] : []
            }
            startTime={startTime}
            endTime={endTime}
            commonFilters={commonFilters}
          />
        </div>
      )}
    </>
  );
};
